import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { isNullOrUndefined } from '@sla-ng/core';
import { Moment } from 'moment';
import { BehaviorSubject, distinctUntilChanged, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICostCenterReferenceValue } from '../../../../models/cost-center-reference-value.model';
import { ICostCenter } from '../../../../models/cost-center.model';
import { IReferenceValueLayout } from '../../../../models/layout/reference-value-layout.model';
import { ICostCenterState } from '../../../../services/facades/master-data/cost-center-state.model';
import { CostCenterFacade } from '../../../../services/facades/master-data/cost-center.facade';
import { CostCenterLayoutService } from '../../../../services/layout/cost-center-layout.service';
import { AddReferenceValueDialog } from './add-dialog/add-reference-value-dialog.component';

@Component({
    selector: 'app-assigned-reference-figures-dialog',
    templateUrl: './assigned-reference-figures-dialog.component.html',
    styleUrls: [ './assigned-reference-figures-dialog.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignedReferenceFiguresDialogComponent implements OnInit {

    public costCenterState$: Observable<ICostCenterState>;
    public dataSource: MatTableDataSource<ICostCenterReferenceValue> = new MatTableDataSource<ICostCenterReferenceValue>([]);
    public filterFormGroup$: BehaviorSubject<FormGroup> = new BehaviorSubject<FormGroup>(null);
    public layout: IReferenceValueLayout;

    constructor(
        private myMatDialogRef: MatDialogRef<AssignedReferenceFiguresDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public costCenter: ICostCenter,
        private myCostCenterFacade: CostCenterFacade,
        private myCostCenterLayoutService: CostCenterLayoutService,
        private myMatDialog: MatDialog,
    ) {
    }

    public ngOnInit(): void {
        this.layout = this.myCostCenterLayoutService.getReferenceValueLayout();
        this.costCenterState$ = this.myCostCenterFacade.subState();
        this.costCenterState$
            .pipe(
                map((s: ICostCenterState) => s.referencesValuesForCostCenter),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((data: ICostCenterReferenceValue[]) => {
                this.dataSource.data = data;
            });
        void this.myCostCenterFacade.fetchReferenceValues();
    }


    public close(): void {
        this.myMatDialogRef.close(false);
    }


    public applyFilter(filterValue: string): void {
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
        this.dataSource.filter = filterValue;
    }

    public async deleteReferenceValue(element): Promise<void> {
        await this.myCostCenterFacade.deleteBezugsgroessenWert(element);
        await this.myCostCenterFacade.fetchReferenceValues();
    }

    public onFormGroupChange(formGroup: FormGroup): void {

        Object.keys(formGroup.controls).forEach(key => {
            if (formGroup.get(key).value === '') {
                formGroup.get(key).patchValue(undefined, { emitEvent: false });
            }
        });

        formGroup.valueChanges.pipe(
            map((formData: { costAccountingType: string | number, reference: string | number, validity: Moment | string }) => {
                // Clear-button set the fields to empty string instead of undefined/null; Sanitize that here
                const costAccountingType: number = typeof formData.costAccountingType === 'string' ? undefined : formData.costAccountingType;
                const reference: number = typeof formData.reference === 'string' ? undefined : formData.reference;
                const validity: Moment = typeof formData.validity === 'string' ? undefined : formData.validity;
                if (formData.costAccountingType === '') {
                    formGroup.controls[ 'costAccountingType' ].patchValue(undefined, { emitEvent: false });
                }
                if (formData.reference === '') {
                    formGroup.controls[ 'reference' ].patchValue(undefined, { emitEvent: false });
                }
                if (formData.validity === '') {
                    formGroup.controls[ 'validity' ].patchValue(undefined, { emitEvent: false });
                }
                return {
                    costAccountingType,
                    reference,
                    validity,
                };
            }),
            distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        ).subscribe((formData: { costAccountingType: undefined | number, reference: undefined | number, validity: Moment | undefined }) => {
            // Trigger the filterPredicate; The actual filterValue is not used
            void this.myCostCenterFacade.fetchReferenceValues(formData.costAccountingType, formData.reference, formData.validity);
        });

        this.filterFormGroup$.next(formGroup);
    }

    public async onDeleteReferenceValue(referenceValue: ICostCenterReferenceValue): Promise<void> {
        await this.myCostCenterFacade.deleteBezugsgroessenWert(referenceValue);
        this.fetchDataForFilter();
    }

    public onOpenDialog(referenceValue?: ICostCenterReferenceValue): void {
        const dialog: MatDialogRef<AddReferenceValueDialog> = this.myMatDialog.open(AddReferenceValueDialog, {
            width: '600px',
            data: isNullOrUndefined(referenceValue) ? {} : referenceValue,
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                this.fetchDataForFilter();
            }
        });
    }

    private fetchDataForFilter(): void {
        const filterValues: { costAccountingType: number, reference: number, validity: Moment } = this.filterFormGroup$.getValue().getRawValue();
        void this.myCostCenterFacade.fetchReferenceValues(filterValues.costAccountingType, filterValues.reference, filterValues.validity);
    }
}
